import type { ReactNode } from 'react';
import { Card, Group, Skeleton, Stack, Text, Badge } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { broadcasts } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime, formatCount } from '../../../lib/format';
import { DeliveredCell, SendProgress, statusTone } from '../index';
import { ChannelBadge, broadcastName, hasTemplate } from './channel';
import { broadcastFailureText, broadcastStatusLabel } from './delivery';

export interface BroadcastCardsProps {
  rows: broadcasts.Broadcast[];
  loading: boolean;
  onOpen: (b: broadcasts.Broadcast) => void;
  actions: (b: broadcasts.Broadcast) => ReactNode;
  /** What it cost, net of refunds, as the table shows it (4 October 2026: the cards had no spend). */
  spendFor: (b: broadcasts.Broadcast) => string;
}

/**
 * The broadcast list as cards, below 820px.
 *
 * `broadcasts.md` §4 item 18: the six-column table had no scroll container and
 * no card fallback, so the actions column was simply clipped on a phone.
 */
export function BroadcastCards({ rows, loading, onOpen, actions, spendFor }: BroadcastCardsProps) {
  const t = useT();

  if (loading) {
    return <Stack gap="sm">{[0, 1, 2].map((i) => <Skeleton key={i} height={110} radius="md" />)}</Stack>;
  }

  return (
    <Stack gap="sm">
      {rows.map((b) => (
        <Card key={b.id} withBorder>
          <Stack gap={8}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <button type="button" className="rr-broadcasts__card-name" onClick={() => onOpen(b)}>
                  {hasTemplate(b) ? <code>{b.template_code}</code> : broadcastName(t, b)}
                </button>
                <Text role="caption" c="var(--rr-color-text-muted)">{formatDateTime(b.created_at)}</Text>
              </Stack>
              {actions(b)}
            </Group>

            <Group gap={8} wrap="wrap" align="center">
              <ChannelBadge broadcast={b} t={t} />
              <StatusBadge status={statusTone(b.status_code)}>
                {broadcastStatusLabel(t, b.status_code)}
              </StatusBadge>
              {b.recipients_failed > 0 && (
                <Badge size="xs" color="red" variant="light">
                  {t('broadcasts.failedCount', { count: formatCount(b.recipients_failed) })}
                </Badge>
              )}
            </Group>

            {b.status_code === 'failed' && (
              <Text role="caption" c="var(--rr-color-error)">{broadcastFailureText(t, b)}</Text>
            )}

            <SendProgress broadcast={b} />

            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
              <Stack gap={0}>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.recipient.delivered')}</Text>
                <DeliveredCell broadcast={b} />
              </Stack>
              <Stack gap={0} align="flex-end">
                <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.columns.spend')}</Text>
                <Text role="bodySmall" fw={600} data-testid="broadcast-card-spend">{spendFor(b)}</Text>
              </Stack>
            </Group>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
